import { DATA_COLLECTOR } from '../constants'
import { lazy } from 'react'
import { RouteProps } from 'react-router'

export interface PrivateRouteObject extends RouteProps {
  exact: boolean
  path: string
  breadcrumb: string
  component: any
  title: string
  hidePageWrap?: boolean
  withHeader?: boolean
  canGoBack?: boolean
}

const Login = lazy(() => import('../containers/Login'))
const ConfirmEmail = lazy(() => import('../containers/ConfirmEmail'))
const ForgotPassword = lazy(() => import('../containers/ForgotPassword'))
const ResetPassword = lazy(() => import('../containers/ResetPassword'))
const Tasks = lazy(() => import('../containers/Tasks'))
const CreateTaskType = lazy(() => import('../containers/CreateTaskType'))
const SingleTask = lazy(() => import('../containers/TaskDetails'))
const TaskBuilder = lazy(() => import('../containers/TaskBuilder'))
const TaskTypeDetails = lazy(() => import('../containers/TaskTypeDetails'))
const Projects = lazy(() => import('../containers/Projects'))
const CreateProject = lazy(() => import('../containers/CreateProject'))
const EditProject = lazy(() => import('../containers/EditProject'))
const ProjectDetails = lazy(() => import('../containers/ProjectDetails'))
const Communities = lazy(() => import('../containers/Communities'))
const CommunityDetails = lazy(() => import('../containers/CommunityDetails'))
const CommunityCreate = lazy(() => import('../containers/CreateCommunity'))
const CommunityEdit = lazy(() => import('../containers/CommunityEdit'))
const CommunityMemberProfile = lazy(() => import('../containers/CommunityMemberProfile'))
const Settings = lazy(() => import('../containers/Settings'))
const Help = lazy(() => import('../containers/Help'))
const CreateWithTemplates = lazy(() => import('../containers/CreateWithTemplates'))
const BaoriderProfile = lazy(() => import('../containers/DataCollectorsDetails'))
const Baoriders = lazy(() => import('../containers/DataCollectors'))
const Map = lazy(() => import('../containers/Map'))
const Insights = lazy(() => import('../containers/Dashboard'))
const InsightAnalytics = lazy(() => import('../containers/InsightAnalytics'))
const InsightAnalyticsNew = lazy(() => import('../containers/InsightAnalyticsNew'))
const InsightReportView = lazy(() => import('../containers/InsightReportView'))
const InsightReportEdit = lazy(() => import('../containers/InsightReportEdit'))
const Notifications = lazy(() => import('../features/notifications'))

// Onboarding Routes
const Register = lazy(() => import('../containers/Register'))
const OnboardingLandingPage = lazy(() => import('../containers/OnboardingLandingPage'))

const PRIVATE_ROUTES: PrivateRouteObject[] = [
  {
    exact: true,
    path: '/auth/map',
    breadcrumb: 'Map View',
    component: Map,
    title: 'Map View',
    withHeader: false,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/insights',
    breadcrumb: 'Team Impact',
    component: Insights,
    title: 'Team Impact',
    withHeader: false,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/insight-analytics',
    breadcrumb: 'Analytics',
    component: InsightAnalytics,
    title: 'Analytics',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/insight-analytics/new',
    breadcrumb: 'New Analysis',
    component: InsightAnalyticsNew,
    title: 'New Analysis',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/insight-analytics/report/:id',
    breadcrumb: 'Report',
    component: InsightReportView,
    title: 'Report',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/insight-analytics/report/:id/edit',
    breadcrumb: 'Edit Report',
    component: InsightReportEdit,
    title: 'Edit Report',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/task-types/create-with-templates',
    breadcrumb: 'Create With Templates',
    component: CreateWithTemplates,
    title: 'Create With Templates',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/help',
    breadcrumb: 'Help',
    component: Help,
    title: 'Help',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/',
    breadcrumb: 'Tasks',
    component: Tasks,
    title: 'Tasks',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/tasks',
    breadcrumb: 'Tasks',
    component: Tasks,
    title: 'Tasks',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/task-types-list/create-task',
    breadcrumb: 'Tasks',
    component: CreateTaskType,
    title: 'Tasks',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/task-types-list/detail/:id',
    breadcrumb: 'Task',
    component: TaskTypeDetails,
    title: 'Task',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/task-types-list',
    breadcrumb: 'task-builder',
    component: TaskBuilder,
    title: 'Tasks Types',
    withHeader: false,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/tasks/:id',
    breadcrumb: 'Task-Details',
    component: SingleTask,
    title: 'Task',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/create-project',
    breadcrumb: 'New Project',
    component: CreateProject,
    title: 'Create Project',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/copy-project/:id',
    breadcrumb: 'Copy Project',
    component: CreateProject,
    title: 'Copy Project',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/project/:id',
    breadcrumb: 'Edit Project',
    component: EditProject,
    title: 'Edit Project',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/projects/:id',
    breadcrumb: 'Project Details',
    component: ProjectDetails,
    title: 'Project Details',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/projects',
    breadcrumb: 'Projects',
    component: Projects,
    title: 'Projects',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/communities',
    breadcrumb: 'Communities',
    component: Communities,
    title: 'Communities',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/create-community',
    breadcrumb: 'Create Team',
    component: CommunityCreate,
    title: 'Create Team',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/communities/:id',
    breadcrumb: 'Community Details',
    component: CommunityDetails,
    title: 'Community Details',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/edit-community/:id',
    breadcrumb: 'Edit Community',
    component: CommunityEdit,
    title: 'Edit Community',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/community-member/:communityId/:id',
    breadcrumb: 'Community Member Details',
    component: CommunityMemberProfile,
    title: 'Community Member',
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/baoriders/:id',
    breadcrumb: DATA_COLLECTOR + ' Details',
    component: BaoriderProfile,
    title: DATA_COLLECTOR,
    withHeader: true,
    canGoBack: true
  },
  {
    exact: true,
    path: '/auth/baoriders',
    breadcrumb: DATA_COLLECTOR + 's',
    component: Baoriders,
    title: DATA_COLLECTOR + 's',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/settings',
    breadcrumb: 'Settings',
    component: Settings,
    title: 'Settings',
    withHeader: true,
    canGoBack: false
  },
  {
    exact: true,
    path: '/auth/onboarding',
    breadcrumb: 'Onboarding',
    component: OnboardingLandingPage,
    title: 'Onboarding',
    withHeader: true,
    canGoBack: false,
    hidePageWrap: true
  },
  {
    exact: true,
    path: '/auth/notifications',
    breadcrumb: 'Notifications',
    component: Notifications,
    title: 'Notifications',
    withHeader: true,
    canGoBack: false
  }
]

const PUBLIC_ROUTES = [
  {
    exact: true,
    title: 'Login',
    path: '/',
    component: Login
  },
  {
    exact: true,
    title: 'Login',
    path: '/login',
    component: Login
  },
  {
    exact: true,
    title: 'Confirm Email',
    path: '/confirm-email',
    component: ConfirmEmail
  },
  {
    exact: true,
    title: 'Forgot Password',
    path: '/forgot-password',
    component: ForgotPassword
  },
  {
    exact: true,
    title: 'Reset Password',
    path: '/reset-password',
    component: ResetPassword
  },
  {
    exact: true,
    title: 'Register',
    path: '/register',
    component: Register
  }
]

export { PUBLIC_ROUTES, PRIVATE_ROUTES }
